import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, RotateCcw, RotateCw, AlertTriangle } from 'lucide-react';

/* Recording player with always-visible speed controls. Also handles MediaRecorder
   webm files whose duration the browser reports as Infinity (the reason the native
   player hides its speed menu on some recordings): we seek to the end once to
   discover the real length. The chosen speed is remembered across recordings. */
const RATES = [0.75, 1, 1.25, 1.5, 2];
const KEY = 'cmx_audio_rate';
const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};
const savedRate = () => {
  try { const v = parseFloat(localStorage.getItem(KEY)); return RATES.includes(v) ? v : 1; } catch (_) { return 1; }
};

export default function AudioPlayer({ src, testId = 'audio-player' }) {
  const ref = useRef(null);
  const probing = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRateState] = useState(savedRate);
  const [error, setError] = useState(false);

  useEffect(() => { if (ref.current) ref.current.playbackRate = rate; }, [rate, src]);
  useEffect(() => { setTime(0); setDuration(0); setPlaying(false); setError(false); probing.current = false; }, [src]);

  const readDuration = () => {
    const a = ref.current;
    if (a && Number.isFinite(a.duration) && a.duration > 0) { setDuration(a.duration); return true; }
    return false;
  };
  const onLoaded = () => {
    const a = ref.current;
    if (!a || readDuration()) return;
    probing.current = true;
    a.currentTime = 1e101;
  };
  const finishProbe = () => {
    const a = ref.current;
    if (!a || !probing.current) return;
    probing.current = false;
    readDuration();
    a.currentTime = 0;
  };
  const setRate = (r) => {
    setRateState(r);
    try { localStorage.setItem(KEY, String(r)); } catch (_) { /* ignore */ }
  };
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => setError(true)); else a.pause();
  };
  const skip = (d) => {
    const a = ref.current;
    if (!a) return;
    const max = duration || a.duration;
    a.currentTime = Math.max(0, Math.min(Number.isFinite(max) ? max : Infinity, a.currentTime + d));
  };
  const seek = (e) => { const a = ref.current; if (a) a.currentTime = parseFloat(e.target.value); };

  if (!src) return null;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3 space-y-2" data-testid={testId}>
      <audio ref={ref} src={src} preload="metadata" onLoadedMetadata={onLoaded} onDurationChange={readDuration} onSeeked={finishProbe}
        onTimeUpdate={() => { if (probing.current) finishProbe(); else if (ref.current) setTime(ref.current.currentTime); }}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} onError={() => setError(true)} />
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => skip(-5)} className="p-1.5 rounded-md text-neutral-500 hover:bg-neutral-100" title="Back 5 seconds" aria-label="Back 5 seconds" data-testid={`${testId}-back`}><RotateCcw size={15} /></button>
        <button type="button" onClick={toggle} className="w-9 h-9 rounded-full bg-neutral-900 text-white flex items-center justify-center hover:bg-neutral-700 shrink-0" aria-label={playing ? 'Pause' : 'Play'} data-testid={`${testId}-toggle`}>
          {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
        </button>
        <button type="button" onClick={() => skip(5)} className="p-1.5 rounded-md text-neutral-500 hover:bg-neutral-100" title="Forward 5 seconds" aria-label="Forward 5 seconds" data-testid={`${testId}-fwd`}><RotateCw size={15} /></button>
        <span className="text-xs font-mono text-neutral-600 w-10 text-right tabular-nums" data-testid={`${testId}-time`}>{fmt(time)}</span>
        <input type="range" min={0} max={duration || 0} step={0.1} value={Math.min(time, duration || 0)} onChange={seek} disabled={!duration}
          className="flex-1 accent-neutral-900 disabled:opacity-40" aria-label="Seek" data-testid={`${testId}-seek`} />
        <span className="text-xs font-mono text-neutral-400 w-10 tabular-nums" data-testid={`${testId}-duration`}>{duration ? fmt(duration) : '--:--'}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400 mr-1">Speed</span>
        {RATES.map((r) => (
          <button key={r} type="button" onClick={() => setRate(r)} data-testid={`${testId}-rate-${String(r).replace('.', '_')}`}
            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition ${rate === r ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-100'}`}>
            {r}×
          </button>
        ))}
        {error && <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-rose-600" data-testid={`${testId}-error`}><AlertTriangle size={12} /> Recording could not be loaded</span>}
      </div>
    </div>
  );
}
